/**
 * How two supervisory ratings move, in the bank's own numbers (09.1 E9, E10): earnings follows the trailing return on assets by a rule, one notch a quarter; liquidity after a deposit run
 * is held until funding and cover recover. Rules come from the content parameters (`PreviewInput.params.supervisory`), the bank's standing from the report's `CamelsState`. Pure.
 */
import type { RoundReport } from "@bsim/contracts";
import type { ContentParams } from "@bsim/domain";
import { formatRate } from "../format";
import { balanceTotals } from "../financials/balance";
import { incomeValues } from "../financials/statements";

type Supervisory = ContentParams["supervisory"];
type Camels = RoundReport["scorecard"]["camels"];

/** The trailing return on assets over the report's quarters (the year's four): their net income over the year-end assets; null without statements. */
export function trailingRoa(report: RoundReport): { readonly roaRate: number; readonly lossQuarter: boolean } | null {
  const last = report.quarterly.at(-1);
  if (last === undefined) return null;
  const incomes = report.quarterly.map((q) => incomeValues(q.statements)["netIncome"] ?? 0);
  const assets = balanceTotals(last.statements).assetsUsd;
  return assets <= 0 ? null : { roaRate: incomes.reduce((a, b) => a + b, 0) / assets, lossQuarter: incomes.some((n) => n < 0) };
}

/** The rating the earnings rule gives a return on assets: the first band it clears (1 best), a loss quarter no better than the rule's loss rating. */
export function earningsRuleRating(roaRate: number, lossQuarter: boolean, rule: NonNullable<Supervisory["earningsRule"]>): number {
  const [top = 0, mid = 0, low = 0] = rule.roa4qBandsRate;
  const rated = roaRate >= top ? 1 : roaRate >= mid ? 2 : roaRate >= low ? 3 : 4;
  return lossQuarter ? Math.max(rated, rule.lossQuarterRating) : rated;
}

/** "Earnings: a return on assets of 0.52% over the last four quarters rates 3. The rating falls one notch a quarter at most and rises only after 2 better quarter-ends (1 so far); yours is 3." */
export function earningsLine(report: RoundReport, rule: Supervisory["earningsRule"] | undefined): string | null {
  const roa = trailingRoa(report);
  if (roa === null || rule === undefined) return null;
  const { camels } = report.scorecard;
  const better = camels.earningsPendingBetterQuarters ?? 0;
  const notch = rule.maxDownNotchesPerQuarter === 1 ? "one notch a quarter" : `${rule.maxDownNotchesPerQuarter} notches a quarter`;
  return `Earnings: a return on assets of ${formatRate(roa.roaRate, 2)} over the last four quarters rates ${earningsRuleRating(roa.roaRate, roa.lossQuarter, rule)}${roa.lossQuarter ? " (a loss quarter caps it)" : ""}. The rating falls ${notch} at most and rises only after ${rule.upgradeAfterQuarters} better quarter-ends${better > 0 ? ` (${better} so far)` : ""}; yours is ${camels.components.E}.`;
}

/** "Liquidity: after a deposit run the rating is held at 3 until cover has been at target for 2 quarter-ends and wholesale funding is under 12% of liabilities." Only while a run is on record. */
export function liquidityLine(camels: Camels, rule: Supervisory["liquidityAfterRun"] | undefined): string | null {
  const run = camels.liquidityAfterRun;
  if (run === undefined || rule === undefined) return null;
  const history = run.coverAtTargetQuarterEnds;
  const atTarget = history.length - (history.lastIndexOf(false) + 1);
  const waited = history.length >= rule.minQuarterEnds ? "" : ` for at least ${rule.minQuarterEnds} quarter-ends and`;
  return `Liquidity: after the deposit run the rating is held at ${run.floor}${waited} until cover has been at target for ${rule.upgradeCoverageAtTargetQuarterEnds} quarter-ends (${Math.min(atTarget, rule.upgradeCoverageAtTargetQuarterEnds)} so far) and wholesale funding is under ${formatRate(rule.upgradeWholesaleShareOfLiabilitiesBelowRate, 0)} of liabilities; yours is ${camels.components.L}.`;
}

/**
 * "Supervisors rate liquidity 2 from 110% coverage; yours is 105%, in the 3 band." The coverage cut-offs are the content's (`thresholds.L`, lcrLiteRate), never typed here.
 * Said only while liquidity was rated on coverage (the rated measure's KPI) and a better band exists. Null otherwise.
 */
export function coverageCutoffLine(camels: Pick<Camels, "ratedOn">, supervisory: Supervisory | undefined): string | null {
  const on = camels.ratedOn?.L;
  const cutoffs = supervisory?.thresholds["L"]?.find((row) => row.metric === "lcrLiteRate")?.cutoffs;
  if (on?.kpi !== "lcrLite" || cutoffs === undefined) return null;
  const band = 1 + cutoffs.filter((c) => on.value < c).length; // the rating the reading falls in (1 best)
  const better = cutoffs[band - 2];
  if (better === undefined) return null;
  return `Supervisors rate liquidity ${band - 1} from ${formatRate(better, 0)} coverage; yours is ${formatRate(on.value, 0)}, in the ${band} band.`;
}
